import { TmcContextService } from './../../../../common/tmc-context.service';
import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { StationGroup } from '../../../../client/model/station-group';
import { TransportOption } from '../../../../client/model/transport-option';

import { TranslationService } from '@sdbn/common';
import { ReportFilterModel, ReportFilterTimeframeType, ReportFilterType } from '../report-filter.model';
import { DateTime } from 'luxon';
import { StructureNode } from '../../../../components/structure-node-selection/structure-node-selection.model';

export class ReportFilterDetailsDialogData {
  dashboardFilterModel: ReportFilterModel;
}

export class OptionsColumn {
  columnIndex: number;
  transportOptions = new Array<TransportOption>();
}

@Component({
    selector: 'app-report-filter-details-dialog',
    templateUrl: './report-filter-details-dialog.component.html',
    styleUrls: ['./report-filter-details-dialog.component.scss'],
    standalone: false
})
export class ReportFilterDetailsDialogComponent implements OnInit, OnDestroy {

  reportFilterType = ReportFilterType;

  reportFilterTimeframeTypeOptions = new Array<ReportFilterTimeframeType>();

  selectedStructureNode: StructureNode;

  filterModel = new ReportFilterModel();

  OPTIONS_FORM_PREFIX = 'option_';
  filterDetailsFormGroup = new FormGroup({});

  tmcContextSubscription: Subscription;

  transportOptions: TransportOption[];
  columns = new Array<OptionsColumn>();

  stationGroups = new Array<StationGroup>();

  noFilterVisible = true;
  optionsFilterVisible = false;
  structureFilterVisible = false;

  dayVisible = true;
  monthVisible = true;
  yearVisible = true;

  constructor(
    public dialogRef: MatDialogRef<ReportFilterDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportFilterDetailsDialogData,
    public translationService: TranslationService,
    private tmcContextService: TmcContextService
  ) {

    for (const key of Object.keys(ReportFilterTimeframeType)) {
      this.reportFilterTimeframeTypeOptions.push(ReportFilterTimeframeType[key]);
    }

    if (this.data.dashboardFilterModel != null) {
      this.filterModel = new ReportFilterModel(data.dashboardFilterModel);

      const date = DateTime.fromFormat(new DatePipe('de-DE').transform(new Date(), 'd.MM.yyyy H:mm'), 'd.MM.yyyy H:mm');
      if (this.filterModel.day == null) {
        this.filterModel.day = date.day;
      }
      if (this.filterModel.month == null) {
        this.filterModel.month = date.month;
      }
      if (this.filterModel.year == null) {
        this.filterModel.year = date.year;
      }

      if (this.filterModel.day == null) {
        this.filterModel.day = date.day;
      }
      if (this.filterModel.month == null) {
        this.filterModel.month = date.month;
      }
      if (this.filterModel.year == null) {
        this.filterModel.year = date.year;
      }
    }

    this.tmcContextSubscription = this.tmcContextService.updated.subscribe(next => {
      if (next != null) {
        this.transportOptions = tmcContextService.getYardConfiguration().transportOptions;
        this.stationGroups = tmcContextService.getYardConfiguration().stationGroups;
        this.prepareOptionsColumns();
        this.initializeFilterDetailsFormGroup();
        this.resetFilterDetailsFormValues();
        this.restoreStructureNodeSelection();
      }
    });
  }

  ngOnInit(): void {
  }

  ngOnDestroy() {
    if (this.tmcContextSubscription) {
      this.tmcContextSubscription.unsubscribe();
    }
  }

  public cancelDialog() {
    this.dialogRef.close();
  }

  public confirmFilter() {
    this.dialogRef.close(this.filterModel);
  }

  public onSelectedStructureChanged(structureNode: StructureNode) {
    this.selectedStructureNode = structureNode;
    this.filterModel.stationGroupId = structureNode.stationGroupId;
    this.filterModel.stationSetId = structureNode.stationSetId;
  }

  public onFilterTypeClicked(o) {
    this.filterDetailsFormGroup.controls.timeframeType.setValue(o);
  }

  private initializeFilterDetailsFormGroup() {

    this.filterDetailsFormGroup.addControl('filterType', new FormControl(null, []));
    this.filterDetailsFormGroup.addControl('timeframeType', new FormControl(null, [Validators.required]));
    this.filterDetailsFormGroup.addControl('day', new FormControl(null, [Validators.required]));
    this.filterDetailsFormGroup.addControl('month', new FormControl(null, [Validators.required]));
    this.filterDetailsFormGroup.addControl('year', new FormControl(null, [Validators.required]));

    for (const parentOption of this.transportOptions) {
      this.filterDetailsFormGroup.addControl(this.OPTIONS_FORM_PREFIX + parentOption.id, new FormControl(null, []));

      for (const co of parentOption.transportOptions) {
        this.filterDetailsFormGroup.addControl(this.OPTIONS_FORM_PREFIX + co.id, new FormControl(null, []));
      }
    }

    this.filterDetailsFormGroup.valueChanges.subscribe(

      value => {

        this.filterModel.filterType = this.filterDetailsFormGroup.controls.filterType.value;
        this.filterModel.timeframeType = this.filterDetailsFormGroup.controls.timeframeType.value;
        this.filterModel.day = this.filterDetailsFormGroup.controls.day.value;
        this.filterModel.month = this.filterDetailsFormGroup.controls.month.value;
        this.filterModel.year = this.filterDetailsFormGroup.controls.year.value;

        if (this.filterDetailsFormGroup.dirty) {
          this.filterModel.transportOptionIds.clear();
          for (const po of this.transportOptions) {
            if (
              this.filterDetailsFormGroup.controls[this.OPTIONS_FORM_PREFIX + po.id] != null &&
              this.filterDetailsFormGroup.controls[this.OPTIONS_FORM_PREFIX + po.id].value === true) {

              this.filterModel.transportOptionIds.add(po.id);

              for (const co of po.transportOptions) {
                if (this.filterDetailsFormGroup.controls[this.OPTIONS_FORM_PREFIX + co.id] != null &&
                  this.filterDetailsFormGroup.controls[this.OPTIONS_FORM_PREFIX + co.id].value === true) {
                  this.filterModel.transportOptionIds.add(co.id);
                }
              }
            }
          }
        }
        this.updateFilterVisibility();
        this.updateDateInputVisibility();
      }
    );
  }

  private resetFilterDetailsFormValues() {
    const values: any = {};

    values.filterType = this.filterModel.filterType;
    values.timeframeType = this.filterModel.timeframeType;
    values.day = this.filterModel.day;
    values.month = this.filterModel.month;
    values.year = this.filterModel.year;

    for (const po of this.transportOptions) {

      values[this.OPTIONS_FORM_PREFIX + po.id] = this.filterModel.transportOptionIds.has(po.id);

      for (const co of po.transportOptions) {
        values[this.OPTIONS_FORM_PREFIX + co.id] = this.filterModel.transportOptionIds.has(co.id);
      }
    }


    this.filterDetailsFormGroup.reset(values);
    this.updateFilterVisibility();
    this.updateDateInputVisibility();
  }

  private restoreStructureNodeSelection() {
    this.selectedStructureNode = null;
    for (const sg of this.stationGroups) {
      if (this.filterModel.stationGroupId != null && this.filterModel.stationGroupId === sg.id) {
        this.selectedStructureNode = new StructureNode(sg.name, sg.id, null);
        return;
      }
      for (const set of sg.stationSets) {
        if (this.filterModel.stationSetId != null && this.filterModel.stationSetId === set.id) {
          this.selectedStructureNode = new StructureNode(sg.name, null, set.id);
          return;
        }
      }
    }
  }

  private updateFilterVisibility() {
    this.noFilterVisible = this.filterDetailsFormGroup.controls.filterType.value === ReportFilterType.none;
    this.optionsFilterVisible = this.filterDetailsFormGroup.controls.filterType.value === ReportFilterType.options;
    this.structureFilterVisible = this.filterDetailsFormGroup.controls.filterType.value === ReportFilterType.structure;
  }

  private updateDateInputVisibility() {
    if (this.filterModel.timeframeType === ReportFilterTimeframeType.day_by_daytime) {
      this.dayVisible = true;
      this.monthVisible = true;
      this.yearVisible = true;
    } else if (this.filterModel.timeframeType === ReportFilterTimeframeType.weekday_month_by_daytime ||
      this.filterModel.timeframeType === ReportFilterTimeframeType.month_by_daytime ||
      this.filterModel.timeframeType === ReportFilterTimeframeType.month_by_day) {
      this.dayVisible = false;
      this.monthVisible = true;
      this.yearVisible = true;
    } else if (this.filterModel.timeframeType === ReportFilterTimeframeType.weekday_year_by_daytime ||
      this.filterModel.timeframeType === ReportFilterTimeframeType.year_by_daytime ||
      this.filterModel.timeframeType === ReportFilterTimeframeType.year_by_month) {
      this.dayVisible = false;
      this.monthVisible = false;
      this.yearVisible = true;
    }

    if (this.dayVisible) {
      this.filterDetailsFormGroup.controls.day.enable({ onlySelf: false, emitEvent: false });
    } else {
      this.filterDetailsFormGroup.controls.day.disable({ onlySelf: false, emitEvent: false });
    }
    if (this.monthVisible) {
      this.filterDetailsFormGroup.controls.month.enable({ onlySelf: false, emitEvent: false });
    } else {
      this.filterDetailsFormGroup.controls.month.disable({ onlySelf: false, emitEvent: false });
    }
    if (this.yearVisible) {
      this.filterDetailsFormGroup.controls.year.enable({ onlySelf: false, emitEvent: false });
    } else {
      this.filterDetailsFormGroup.controls.year.disable({ onlySelf: false, emitEvent: false });
    }
  }

  private prepareOptionsColumns() {

    const optionsColumns = new Array<OptionsColumn>();

    const columnsByColumnIndex = new Map<number, OptionsColumn>();
    for (const o of this.transportOptions) {
      let column = columnsByColumnIndex.get(o.columnIndex);
      if (column == null) {
        column = new OptionsColumn();
        column.columnIndex = o.columnIndex;
        columnsByColumnIndex.set(o.columnIndex, column);
        optionsColumns.push(column);
      }
      column.transportOptions.push(o);
    }

    // sortieren
    optionsColumns.sort((a: OptionsColumn, b: OptionsColumn) => {
      return a.columnIndex - b.columnIndex;
    });
    optionsColumns.forEach(
      col => {
        col.transportOptions = col.transportOptions.sort((a: TransportOption, b: TransportOption) => {
          return a.rowIndex - b.rowIndex;
        });
        col.transportOptions.forEach(
          po => {
            po.transportOptions = po.transportOptions.sort((a: TransportOption, b: TransportOption) => {
              return a.rowIndex - b.rowIndex;
            });
          }
        );
      }
    );

    this.columns = optionsColumns;
  }
}
